import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";

const fetchWithAuth = (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...options.headers },
  });
};

export interface JuiceSize {
  label: string;
  ml: number;
}

export interface Container {
  label: string;   // e.g. "Jug", "Bucket"
  litres: number;  // e.g. 5
}

export interface CompanyPreferences {
  auto_sku: boolean;
  show_supplier: boolean;
  show_category: boolean;
  staff_nav: string[];
  // Per-user page grants: userId -> granted paths. A user present here sees
  // exactly their list (plus Settings); everyone else falls back to staff_nav.
  staff_nav_users: Record<string, string[]>;
  juice_sizes: JuiceSize[];
  containers: Container[];
  openrouter_api_key: string;
}

const DEFAULT_PREFS: CompanyPreferences = {
  auto_sku: true,
  show_supplier: false,
  show_category: false,
  staff_nav: [],
  staff_nav_users: {},
  juice_sizes: [],
  containers: [],
  openrouter_api_key: "",
};

/**
 * Paths the given user may see, or null for unrestricted access.
 *
 * - Admins are never restricted.
 * - A user with their own entry in staff_nav_users sees exactly that list
 *   (an empty selection still leaves the Dashboard so the router can't
 *   dead-end on a redirect to a blocked page).
 * - Everyone else follows the general staff_nav list; empty means unrestricted.
 */
export function resolveStaffNav(
  preferences: Pick<CompanyPreferences, "staff_nav" | "staff_nav_users">,
  user: { id?: number } | null,
  isAdmin: boolean,
): string[] | null {
  if (isAdmin || !user?.id) return null;

  const perUser = preferences.staff_nav_users?.[String(user.id)];
  if (perUser !== undefined) {
    return perUser.length > 0 ? perUser : ["/"];
  }

  const general: string[] = preferences.staff_nav || [];
  return general.length > 0 ? general : null;
}

export function useCompanyPreferences() {
  const queryClient = useQueryClient();

  const { data: preferences = DEFAULT_PREFS } = useQuery<CompanyPreferences>({
    queryKey: ["company-preferences"],
    queryFn: async () => {
      const res = await fetchWithAuth(`${API_BASE_URL}/v1/companies/preferences/me`);
      if (!res.ok) return DEFAULT_PREFS;
      const data = await res.json();
      return { ...DEFAULT_PREFS, ...data };
    },
    staleTime: 5 * 60 * 1000, // 5 min — preferences rarely change
  });

  const updateMutation = useMutation({
    mutationFn: async (prefs: Partial<CompanyPreferences>) => {
      const res = await fetchWithAuth(`${API_BASE_URL}/v1/companies/preferences/me`, {
        method: "PATCH",
        body: JSON.stringify({ ...preferences, ...prefs }),
      });
      if (!res.ok) throw new Error("Failed to update preferences");
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["company-preferences"], data);
    },
  });

  return { preferences, updatePreferences: updateMutation.mutate, isUpdating: updateMutation.isPending };
}
